import { Disclosure, Transition } from "@headlessui/react";
import Link from "next/link";
import {
  FacilityProps,
  FaqProps,
  UpcomingAuctionProps,
} from "../../../helpers/types/getTypes";
import {
  AddBoldCircleIcon,
  ArrowDownBoldIcon,
  CloseCircleIcon,
  LocationBoldIcon,
  SaveAddIcon,
} from "../icon/SvgIcon";
import Image from "../image/Image";

export const CardAuction = ({}: {}) => {
  return (
    <section className="flex flex-row items-center space-x-2 w-[15.375rem] min-w-fit">
      <div className="min-w-[3.375rem]">
        <Image
          src="/images/img-example.png"
          alt=""
          className="w-[3.375rem] object-cover rounded-xl"
        />
      </div>
      <div className="flex flex-col space-y-2">
        <div className="text-regular-normal font-medium text-neutral-900 break-all line-clamp-1">
          Central xxxxx xxxx xxxx
        </div>
        <div className="flex flex-row items-center space-x-1.5">
          <div className="text-neutral-900">
            <LocationBoldIcon size="16" />
          </div>
          <div className="break-all line-clamp-1 text-tiny-none text-neutral-800">
            Fresno, xxxxx xxxxx xxxx
          </div>
        </div>
      </div>
    </section>
  );
};

export const CardAuctionDetail = ({ data }: { data: FacilityProps }) => {
  return (
    <Link href={`/facility/detail/${data.slug}`}>
      <a className="flex flex-row items-center space-x-2 w-[15.375rem] min-w-fit">
        <div className="min-w-[4.25rem]">
          <Image
            src={data.image.md}
            alt={data.name}
            className="w-[4.25rem] object-cover rounded-xl"
          />
        </div>
        <div className="flex flex-col space-y-1.5">
          <div className="text-regular-normal font-medium text-neutral-900 break-all line-clamp-1">
            {data.name}
          </div>
          <div className="font-bold text-primary-500 text-small-tight">
            #{data.id}{" "}
            <span className="font-normal text-neutral-600 capitalize">
              {data.type
                ? data.type === "in_person"
                  ? "in person"
                  : data.type
                : "Caravan"}{" "}
              Auction
            </span>
          </div>
          <div className="flex flex-row items-center space-x-1.5">
            <div className="text-neutral-900">
              <LocationBoldIcon size="16" />
            </div>
            <div className="break-all line-clamp-1 text-tiny-none text-neutral-800">
              {data.address}
            </div>
          </div>
        </div>
      </a>
    </Link>
  );
};

export const InfoWindowFacility = ({ data }: { data: FacilityProps }) => (
  <div className="bg-white rounded-xl p-3 w-[11.75rem] flex flex-col focus:!outline-none focus-visible:!outline-none">
    <Image
      src={data.image.lg}
      alt={data.name}
      className="w-full h-[5.25rem] rounded-lg object-cover"
    />
    <div className="font-medium text-small-normal text-neutral-900 truncate whitespace-normal line-clamp-2 break-all mt-2">
      {data.name}
    </div>
    <div className="mt-1 font-bold text-primary-500 text-tiny-tight">
      #{data.id}
    </div>
    <div className="mt-1.5 text-neutral-500 text-tiny-tight">
      {data.type
        ? data.type === "in_person"
          ? "in person"
          : data.type
        : "Caravan"}{" "}
      Auction
    </div>
    <div className="text-tiny-normal text-neutral-600 line-clamp-2 break-all mt-1">
      {data.address}
    </div>
  </div>
);

export const CardFacilityDetail = ({
  data,
  isBlur,
}: {
  data: UpcomingAuctionProps;
  isBlur?: boolean;
}) => {
  return (
    <section
      className={`relative flex flex-row items-center justify-between w-full md:w-[33.438rem] min-w-fit bg-white p-3.5 rounded-xl`}
    >
      {isBlur && (
        <div className="absolute left-0 bg-white w-full h-full opacity-50" />
      )}
      <div className="flex flex-row items-center space-x-3.5">
        <div className="w-[5.25rem]">
          <Image
            src={data.image.lg}
            alt={data.unit_number}
            className="w-[5.25rem] object-cover rounded-xl"
          />
        </div>
        <div className="flex flex-col space-y-1.5">
          <div className="text-regular-none md:text-title3 font-bold text-neutral-900 break-all line-clamp-1">
            #{data.unit_number}
          </div>
          <div className="font-medium text-neutral-900 text-small-normal">
            Start this week
          </div>
          <div className="break-all line-clamp-1 text-tiny-normal md:text-regular-normal text-neutral-600">
            1,200 watching this auction
          </div>
        </div>
      </div>
      <div className="border border-neutral-200 rounded-full p-2 text-primary-500 md:hidden">
        <SaveAddIcon size="16" />
      </div>
    </section>
  );
};

export const CardFaq = ({ data }: { data: FaqProps }) => {
  return (
    <div className="w-full">
      <div className="mx-auto w-full rounded-[0.875rem] bg-white px-3 py-3.5">
        <Disclosure>
          {({ open }) => (
            <>
              <Disclosure.Button className="flex w-full justify-between space-x-1 text-left text-[1.375rem] leading-6 font-bold text-primary-900 focus:outline-none">
                <span className="break-words line-clamp-2">
                  {data.attributes.title}
                </span>
                {open ? (
                  <CloseCircleIcon className="text-neutral-500 min-w-[1.5rem]" />
                ) : (
                  <AddBoldCircleIcon className="text-primary-500 min-w-[1.5rem]" />
                )}
              </Disclosure.Button>
              <Transition
                enter="transition ease duration-500 transform"
                enterFrom="opacity-0 -translate-y-12"
                enterTo="opacity-100 translate-y-0"
                leave="transition ease duration-300 transform"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 -translate-y-12"
              >
                <Disclosure.Panel className="mt-4.5 text-regular-normal text-neutral-600">
                  <p className="break-words line-clamp-3">
                    {data.attributes.short_description}
                  </p>
                  <Link href={`/faq/${data.attributes.slug}`}>
                    <a className="font-bold text-primary-500">Read More...</a>
                  </Link>
                </Disclosure.Panel>
              </Transition>
            </>
          )}
        </Disclosure>
      </div>
    </div>
  );
};
